import { useEffect, useState } from 'react';
import type { OutboxStreamKind } from '#shared/core/event-batch';
import { type OutboxStatus, outbox } from './outbox';

/** How many events wait for signal (for the "3 plays waiting for signal" chip). */
export function useOutboxCount(filter?: { kind?: OutboxStreamKind; id?: string }): number {
  const [n, setN] = useState(0);
  const kind = filter?.kind;
  const id = filter?.id;
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      outbox
        .count({ kind, id })
        .then((c) => {
          if (alive) setN(c);
        })
        .catch(() => {});
    };
    refresh();
    const off = outbox.subscribe(refresh);
    return () => {
      alive = false;
      off();
    };
  }, [kind, id]);
  return n;
}

export const EMPTY_STATUS: OutboxStatus = {
  waiting: 0,
  creates: 0,
  rejected: 0,
  flushing: false,
  blocked: null,
  offline: false,
  degraded: false,
};

/** True when two statuses would draw the same chip, so the hook skips a render. */
export function sameStatus(a: OutboxStatus, b: OutboxStatus): boolean {
  return (
    a.waiting === b.waiting &&
    a.creates === b.creates &&
    a.rejected === b.rejected &&
    a.flushing === b.flushing &&
    a.blocked === b.blocked &&
    a.offline === b.offline &&
    a.degraded === b.degraded
  );
}

/**
 * Everything the sync chip needs in one object: waiting plays, parked rows, whether a flush
 * runs, why it is blocked, whether the phone is offline. Refreshes on every outbox change and
 * on the browser's online and offline events.
 */
export function useOutboxStatus(): OutboxStatus {
  const [status, setStatus] = useState<OutboxStatus>(EMPTY_STATUS);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      outbox
        .status()
        .then((s) => {
          if (alive) setStatus((prev) => (sameStatus(prev, s) ? prev : s));
        })
        .catch(() => {});
    };
    refresh();
    const off = outbox.subscribe(refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    return () => {
      alive = false;
      off();
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
    };
  }, []);
  return status;
}
